import { useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PeriodProvider } from "./PeriodContext";
import { PeriodSelector } from "@/components/reports/period-selector/PeriodSelector";
import { MonthlyBudgetPanel } from "@/components/reports/monthly-budget/MonthlyBudgetPanel";
import { AnnualBudgetPanel } from "@/components/reports/annual-budget/AnnualBudgetPanel";
import { TrendProjectionPanel } from "@/components/reports/trend-projection/TrendProjectionPanel";
import { CategoryBreakdownPanel } from "@/components/reports/category-breakdown/CategoryBreakdownPanel";
import { IncomePanel } from "@/components/reports/income/IncomePanel";
import { PortfolioSummaryPanel } from "@/components/reports/portfolio-summary/PortfolioSummaryPanel";
import { ExportDialog } from "@/components/reports/export-dialog/ExportDialog";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { CurrencyToggle } from "@/components/currency-toggle/CurrencyToggle";

function ReportsDashboard() {
  const [exportOpen, setExportOpen] = useState(false);
  const [displayCurrency, setDisplayCurrency] = useState<string>("KES");
  const {
    convert,
    isLoading: ratesLoading,
    error: ratesError,
  } = useExchangeRates(displayCurrency);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <PeriodSelector />
        <div className="flex flex-wrap items-center justify-between gap-4">
          <CurrencyToggle
            baseCurrency={displayCurrency}
            selectedCurrency={displayCurrency}
            onCurrencyChange={setDisplayCurrency}
            disabled={ratesLoading}
            showBalance={false}
          />
          <Button
            onClick={() => setExportOpen(true)}
            variant="outline"
            size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <PortfolioSummaryPanel />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <MonthlyBudgetPanel
            displayCurrency={displayCurrency}
            convert={convert}
          />
          <AnnualBudgetPanel
            displayCurrency={displayCurrency}
            convert={convert}
          />
        </div>
        <TrendProjectionPanel
          displayCurrency={displayCurrency}
          convert={convert}
        />
        <CategoryBreakdownPanel
          displayCurrency={displayCurrency}
          convert={convert}
        />
        <IncomePanel displayCurrency={displayCurrency} convert={convert} />
      </div>

      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} />
    </div>
  );
}

export default function ReportsPage() {
  return (
    <PeriodProvider>
      <ReportsDashboard />
    </PeriodProvider>
  );
}
